export interface CountryConfig {
  code: string;
  name: string;
  currency: string;
  currencySymbol: string;
  locale: string;
  marketplaces: string;
}

export const DEFAULT_COUNTRY_CODE = "US";

export const COUNTRIES: CountryConfig[] = [
  { code: "US", name: "United States", currency: "USD", currencySymbol: "$", locale: "en-US", marketplaces: "eBay, Facebook Marketplace, Craigslist, Mercari" },
  { code: "AU", name: "Australia", currency: "AUD", currencySymbol: "A$", locale: "en-AU", marketplaces: "eBay Australia, Facebook Marketplace, Gumtree" },
  { code: "GB", name: "United Kingdom", currency: "GBP", currencySymbol: "£", locale: "en-GB", marketplaces: "eBay UK, Facebook Marketplace, Gumtree" },
  { code: "CA", name: "Canada", currency: "CAD", currencySymbol: "C$", locale: "en-CA", marketplaces: "eBay Canada, Facebook Marketplace, Kijiji" },
  { code: "NZ", name: "New Zealand", currency: "NZD", currencySymbol: "NZ$", locale: "en-NZ", marketplaces: "Trade Me, Facebook Marketplace" },
  { code: "DE", name: "Germany", currency: "EUR", currencySymbol: "€", locale: "de-DE", marketplaces: "eBay Kleinanzeigen, Facebook Marketplace" },
  { code: "FR", name: "France", currency: "EUR", currencySymbol: "€", locale: "fr-FR", marketplaces: "Leboncoin, Facebook Marketplace, eBay France" },
  { code: "IE", name: "Ireland", currency: "EUR", currencySymbol: "€", locale: "en-IE", marketplaces: "DoneDeal, Facebook Marketplace, eBay" },
  { code: "IN", name: "India", currency: "INR", currencySymbol: "₹", locale: "en-IN", marketplaces: "OLX, Facebook Marketplace, Quikr" },
  { code: "SG", name: "Singapore", currency: "SGD", currencySymbol: "S$", locale: "en-SG", marketplaces: "Carousell, Facebook Marketplace" },
  { code: "JP", name: "Japan", currency: "JPY", currencySymbol: "¥", locale: "ja-JP", marketplaces: "Mercari Japan, Yahoo Auctions, Rakuma" },
];

const numberFormatCache = new Map<string, Intl.NumberFormat>();
const dateTimeFormatCache = new Map<string, Intl.DateTimeFormat>();

function serializeCacheValue(value: unknown): string {
  if (Array.isArray(value)) {
    return `[${value.map((item) => serializeCacheValue(item)).join(",")}]`;
  }

  if (value && typeof value === "object") {
    return serializeCacheObject(value as Record<string, unknown>);
  }

  return JSON.stringify(value);
}

function serializeCacheObject(options: Record<string, unknown>): string {
  return Object.keys(options)
    .sort()
    .map((key) => `${key}:${serializeCacheValue(options[key])}`)
    .join("|");
}

function getNumberFormatter(locale: string, options: Intl.NumberFormatOptions): Intl.NumberFormat {
  const cacheKey = `${locale}::${serializeCacheObject(options as Record<string, unknown>)}`;
  const cached = numberFormatCache.get(cacheKey);
  if (cached) return cached;

  const formatter = new Intl.NumberFormat(locale, options);
  numberFormatCache.set(cacheKey, formatter);
  return formatter;
}

function getDateTimeFormatter(locale: string, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const cacheKey = `${locale}::${serializeCacheObject(options as Record<string, unknown>)}`;
  const cached = dateTimeFormatCache.get(cacheKey);
  if (cached) return cached;

  const formatter = new Intl.DateTimeFormat(locale, options);
  dateTimeFormatCache.set(cacheKey, formatter);
  return formatter;
}

export function getCountryConfig(code: string | null | undefined): CountryConfig {
  return COUNTRIES.find((c) => c.code === code) ?? COUNTRIES.find((c) => c.code === DEFAULT_COUNTRY_CODE)!;
}

export function formatCurrency(value: number, countryCode?: string | null, options: Intl.NumberFormatOptions = {}): string {
  const config = getCountryConfig(countryCode);
  const formatterOptions: Intl.NumberFormatOptions = {
    style: "currency",
    currency: config.currency,
    maximumFractionDigits: 0,
    ...options,
  };

  return getNumberFormatter(config.locale, formatterOptions).format(value);
}

export function formatDate(
  value: string | number | Date,
  countryCode?: string | null,
  options: Intl.DateTimeFormatOptions = {},
): string {
  const config = getCountryConfig(countryCode);
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  const formatterOptions: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "short",
    day: "numeric",
    ...options,
  };

  return getDateTimeFormatter(config.locale, formatterOptions).format(date);
}

export function formatDateTime(
  value: string | number | Date,
  countryCode?: string | null,
  options: Intl.DateTimeFormatOptions = {},
): string {
  const config = getCountryConfig(countryCode);
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  const formatterOptions: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    ...options,
  };

  return getDateTimeFormatter(config.locale, formatterOptions).format(date);
}
